import { Injector, inject } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from 'src/app/modules/auth/services/auth/auth.service';
import { markNotificationTargetRead } from 'src/app/core/services/notification/inbox/mark-target-read';
import { NOTIFICATION_TARGET_PARAM } from 'src/app/core/services/notification/deep-link/notification-target';
import {
  isNotificationDeepLinkKind,
  NOTIFICATION_DEEP_LINK_DATA_KEY,
  NotificationDeepLinkRole,
  notificationDeepLinkParamName,
  resolveNotificationDeepLink,
  TERMINAL_HOME,
} from './notification-deep-link';

/**
 * Kurzpfade der Glocken-/Push-Meldungen außerhalb der Aufgaben (`/absences/open/:id`,
 * `/time/corrections/open/:id`, `/shifts/swaps/open/:id`, `/shifts/week/:isoWeek`,
 * `/absences/review/:id`, `/time/corrections/review/:id`, `/employees/change-requests/open/:id`,
 * `/shifts/open-shifts`). Rendert nie etwas, sondern leitet je Rolle und Gerät weiter
 * (`resolveNotificationDeepLink`). Rolle wie `taskDeepLinkGuard` über den `AuthService`, Gerät wie
 * die Geräteweiche in `app.component.ts` (`Breakpoints.XSmall`/`Small`).
 */
export const notificationDeepLinkGuard: CanActivateFn = (route: ActivatedRouteSnapshot): UrlTree => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const isMobile = inject(BreakpointObserver).isMatched([Breakpoints.XSmall, Breakpoints.Small]);

  const role: NotificationDeepLinkRole = auth.isTerminal
    ? 'terminal'
    : auth.roleMatch(['MANAGER', 'TEAMLEADER'])
      ? 'lead'
      : 'employee';

  const kind = route.data?.[NOTIFICATION_DEEP_LINK_DATA_KEY];
  if (!isNotificationDeepLinkKind(kind)) {
    return router.createUrlTree([role === 'terminal' ? TERMINAL_HOME : '/']);
  }
  const paramName = notificationDeepLinkParamName(kind);
  const raw = paramName ? route.paramMap.get(paramName) : null;
  const date = route.queryParamMap?.get(NOTIFICATION_TARGET_PARAM.date) ?? null;
  const redirect = resolveNotificationDeepLink(kind, role, raw, isMobile, { date });
  // UX-B: Wer den Kurzpfad öffnet (Glocke, Push-Tipp, Link), hat die Meldung gesehen — auch ein
  // Push-Tipp, der bisher nichts als gelesen markierte. Terminal hat keine Glocke.
  const segments = route.url ?? [];
  if (role !== 'terminal' && segments.length) {
    markNotificationTargetRead(inject(Injector), '/' + segments.map((s) => s.path).join('/'));
  }
  return router.createUrlTree([redirect.path], { queryParams: redirect.queryParams });
};
